import { computed } from 'vue';
import { defineStore } from 'pinia';
import { omit } from 'lodash';

import { useLocalStorageRef } from '@satellite/composables/useLocalStorage';

import useAuth from '@/composables/useAuth';

import type { WarehouseDetails } from '@satellite/composables/useWarehousesPresenter';

const WAREHOUSE_STORAGE_KEY = 'warehouse.selectedWarehouse';

export const useWarehouseStore = defineStore('warehouse', () => {
  const { meRef } = useAuth();
  const userIdRef = computed(() => meRef.value?.id);
  const { ref: selectedWarehouse, dispose: disposeSelectedWarehouse } =
    useLocalStorageRef<WarehouseDetails | null>(WAREHOUSE_STORAGE_KEY, null, {
      userId: userIdRef
    });

  const selectedWarehouseId = computed(() => selectedWarehouse.value?.id ?? null);
  const selectedWarehouseName = computed(() => selectedWarehouse.value?.name ?? null);
  const selectedWarehouseTimezone = computed(() => selectedWarehouse.value?.timezone ?? null);
  const selectedWarehouseSettings = computed(() => selectedWarehouse.value?.settings ?? {});
  const selectedWarehouseFeatures = computed(
    () => selectedWarehouse.value?.warehouseFeatures ?? []
  );

  function setSelectedWarehouse(warehouse: WarehouseDetails | null) {
    if (warehouse) {
      selectedWarehouse.value = omit(warehouse, ['createdBy', 'lastChangedBy', 'ccEmails']);
    } else {
      selectedWarehouse.value = null;
    }
  }

  function clearSelectedWarehouse() {
    selectedWarehouse.value = null;
  }

  function cleanup() {
    disposeSelectedWarehouse();
  }

  return {
    selectedWarehouse,
    selectedWarehouseId,
    selectedWarehouseName,
    selectedWarehouseTimezone,
    selectedWarehouseSettings,
    selectedWarehouseFeatures,
    setSelectedWarehouse,
    clearSelectedWarehouse,
    cleanup
  };
});
